import { useEffect, useRef, useState } from 'react'
import { useQuery } from 'react-query'

import { QueryKeys } from '../../../enums/queryKeys'
import { AttachmentZipDownloadState } from '../enums/attachmentZipDownloadState'
import { AttachmentZipDownload, fetchAttachmentZipDownload } from '../query-api'

const POLLING_INTERVAL_MS = 500
const MAX_POLLING_DURATION_MS = 60000

interface UsePollAttachmentZipDownloadProps {
  polling?: boolean
  attachmentZipDownloadId: string | null
  pollingInterval?: number
  maxPollingDuration?: number
  onSuccess: (attachmentZipDownload: AttachmentZipDownload) => void
  onError: () => void
}

export const usePollAttachmentZipDownload = ({
  polling = false,
  attachmentZipDownloadId,
  pollingInterval = POLLING_INTERVAL_MS,
  maxPollingDuration = MAX_POLLING_DURATION_MS,
  onSuccess,
  onError,
}: UsePollAttachmentZipDownloadProps) => {
  const [isActive, setIsActive] = useState(false)
  const doneRef = useRef(false)
  const callbacksRef = useRef({ onSuccess, onError })

  callbacksRef.current = { onSuccess, onError }

  useQuery(
    [QueryKeys.AttachmentZipDownloadPolling, attachmentZipDownloadId],
    () => fetchAttachmentZipDownload(attachmentZipDownloadId ?? ''),
    {
      enabled: !!attachmentZipDownloadId && isActive,
      refetchInterval: pollingInterval,
      refetchIntervalInBackground: false,
      onSuccess: (data) => {
        if (doneRef.current) {
          return
        }

        const attachmentZipDownload = data?.attachmentZipDownload

        if (attachmentZipDownload?.state === AttachmentZipDownloadState.Succeeded) {
          doneRef.current = true
          setIsActive(false)
          callbacksRef.current.onSuccess(attachmentZipDownload)
        } else if (attachmentZipDownload?.state === AttachmentZipDownloadState.Failed) {
          doneRef.current = true
          setIsActive(false)
          callbacksRef.current.onError()
        }
      },
    },
  )

  // Start polling and set up the give-up timeout
  useEffect(() => {
    if (!polling) {
      return
    }

    doneRef.current = false
    setIsActive(true)

    const timer = window.setTimeout(() => {
      if (doneRef.current) {
        return
      }
      doneRef.current = true
      setIsActive(false)
      callbacksRef.current.onError()
    }, maxPollingDuration)

    return () => {
      window.clearTimeout(timer)
      setIsActive(false)
    }
  }, [polling, maxPollingDuration])
}
